import { motion } from "framer-motion";
import { Play, Users, Wand2 } from "lucide-react";
import { EASE, useCalmMotion } from "@/lib/studio";
import { cn } from "@/lib/utils";

const HEIGHT = { "9:16": "aspect-[9/15]", "1:1": "aspect-[4/5]", "16:9": "aspect-square" };
const STATUS = { pending: "In review", rejected: "Not approved" };

function Tag({ children, tone = "dark" }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md",
      tone === "amber" ? "bg-amber-300/90 text-ink-950" : "bg-black/55 text-zinc-100")}>
      {children}
    </span>
  );
}

export function PresetCard({ preset, index = 0, onOpen }) {
  const calm = useCalmMotion();
  return (
    <motion.button type="button" onClick={() => onOpen(preset)} data-testid={`preset-card-${preset.slug || preset.id}`}
      initial={calm ? false : { opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: EASE, delay: (index % 4) * 0.05 }}
      className="group relative mb-3 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-white/[0.08] bg-ink-850 text-left transition-[border-color,box-shadow] duration-300 hover:border-white/25 hover:shadow-[0_20px_40px_-18px_rgba(0,0,0,0.9)] sm:mb-4">
      <div className={cn("relative w-full overflow-hidden bg-[linear-gradient(110deg,#121216_30%,#1e1e24_50%,#121216_70%)] bg-[length:200%_100%] animate-shimmer", HEIGHT[preset.aspect_ratio] || "aspect-[4/5]")}>
        <img src={preset.cover_url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
        <div className="absolute left-2.5 top-2.5 flex flex-wrap gap-1">
          {preset.kind === "video" && <Tag><Play className="h-2.5 w-2.5 fill-current" /> Video · Soon</Tag>}
          {preset.operation === "edit" && <Tag><Wand2 className="h-2.5 w-2.5" /> Edit</Tag>}
          {preset.source === "community" && <Tag><Users className="h-2.5 w-2.5" /> Community</Tag>}
          {STATUS[preset.status] && <Tag tone="amber">{STATUS[preset.status]}</Tag>}
        </div>
        {preset.example_url && (
          <div className="absolute right-2.5 top-2.5 w-[30%] overflow-hidden rounded-lg border border-white/30 shadow-lg" data-testid="preset-before-inset">
            <img src={preset.example_url} alt="Before" loading="lazy" className="aspect-square w-full object-cover" />
            <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[8px] font-bold uppercase tracking-wider text-white">Before</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 p-3">
          <p className="text-sm font-semibold leading-snug tracking-tight text-white">{preset.title}</p>
          <p className="mt-0.5 text-[11px] font-medium tracking-wide text-zinc-400">{preset.category}</p>
        </div>
      </div>
    </motion.button>
  );
}

export function PresetGrid({ presets, onOpen }) {
  return (
    <div className="columns-2 gap-3 sm:gap-4 md:columns-3 lg:columns-4" data-testid="preset-grid">
      {presets.map((p, i) => <PresetCard key={p.id} preset={p} index={i} onOpen={onOpen} />)}
    </div>
  );
}

export function CategoryChips({ categories, value, onChange }) {
  return (
    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0" role="tablist" aria-label="Preset categories" data-testid="preset-category-filter">
      {["All", ...categories].map((c) => (
        <button key={c} type="button" role="tab" aria-selected={value === c} onClick={() => onChange(c)} data-testid={`preset-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={cn("h-8 shrink-0 rounded-full px-3.5 text-xs font-medium transition-colors duration-200",
            value === c ? "bg-white text-ink-950" : "border border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-100")}>
          {c}
        </button>
      ))}
    </div>
  );
}
